import { Box, Paper, Badge, Typography, useTheme } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCart, useFavourites } from 'core'
import { PageURLs } from 'Routes'
import { MOBILE_NAV_BAR_HEIGHT, mobileNavSafeBottom } from '../../constants/mobileNav'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import { motion, AnimatePresence } from 'framer-motion'

const NavItem = ({ icon, activeIcon, label, path, badge, active, onClick }) => {
  const theme = useTheme()

  return (
    <Box
      component={motion.div}
      whileTap={{ scale: 0.92 }}
      onClick={() => onClick(path)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        cursor: 'pointer',
        py: 1,
        position: 'relative',
        gap: 0.25,
      }}
    >
      {/* Active indicator pill */}
      <AnimatePresence>
        {active && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            sx={{
              position: 'absolute',
              top: 6,
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: `${theme.palette.primary.main}18`,
              zIndex: 0,
            }}
          />
        )}
      </AnimatePresence>

      <Badge badgeContent={badge || 0} color="error" max={99} sx={{ zIndex: 1 }}>
        <Box
          component={motion.div}
          animate={{ y: active ? -1 : 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          sx={{
            color: active ? theme.palette.primary.main : theme.palette.text.secondary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {active ? activeIcon : icon}
        </Box>
      </Badge>

      <Typography
        variant="caption"
        sx={{
          fontSize: '0.625rem',
          fontWeight: active ? 700 : 500,
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
          letterSpacing: '0.03em',
          lineHeight: 1,
          zIndex: 1,
          transition: 'color 0.2s ease',
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

const MobileBottomNav = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { quantity } = useCart()
  const { favouritesQuantity } = useFavourites()
  const theme = useTheme()

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const handleNav = (path) => {
    navigate(path)
  }

  const navItems = [
    {
      label: 'Home',
      path: '/',
      icon: <HomeRoundedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <HomeRoundedIcon sx={{ fontSize: 24 }} />,
    },
    {
      label: 'Search',
      path: PageURLs.SearchResults,
      icon: <SearchRoundedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <SearchRoundedIcon sx={{ fontSize: 24 }} />,
    },
    {
      label: 'Wishlist',
      path: PageURLs.Favourites,
      icon: <FavoriteBorderRoundedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <FavoriteRoundedIcon sx={{ fontSize: 24 }} />,
      badge: favouritesQuantity,
    },
    {
      label: 'Cart',
      path: PageURLs.Cart,
      icon: <ShoppingBagOutlinedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <ShoppingBagRoundedIcon sx={{ fontSize: 24 }} />,
      badge: quantity,
    },
    {
      label: 'Profile',
      path: PageURLs.Profile,
      icon: <PersonOutlineRoundedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <PersonRoundedIcon sx={{ fontSize: 24 }} />,
    },
  ]

  return (
    <Paper
      component={motion.div}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30, delay: 0.1 }}
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        borderRadius: '20px 20px 0 0',
        display: { xs: 'flex', md: 'none' },
        minHeight: MOBILE_NAV_BAR_HEIGHT,
        pb: mobileNavSafeBottom,
        background: theme.palette.mode === 'dark'
          ? 'rgba(15, 22, 41, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: 'none',
        borderTop: `1px solid ${
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'
        }`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 -4px 24px rgba(0,0,0,0.45)'
          : '0 -4px 24px rgba(99,102,241,0.12)',
        overflow: 'hidden',
      }}
    >
      {navItems.map((item) => (
        <NavItem
          key={item.path}
          {...item}
          active={isActive(item.path)}
          onClick={handleNav}
        />
      ))}
    </Paper>
  )
}

export default MobileBottomNav
